import { useTranslation } from "react-i18next";

import {
  orgSettingsChangedTabs,
  type OrgSettingsChangedField,
} from "@/features/identity-hub/lib/org-settings-dirty";
import { orgSettingsFieldLabel, orgSettingsTabLabel } from "@/features/identity-hub/lib/org-settings-field-labels";

/**
 * Footer summary of pending org-settings edits — names the fields while they sit on one tab
 * ("● 1 unsaved change · Accent color") and the tabs once they span several.
 */
export function OrgSettingsDirtyStatus({ changedFields }: { changedFields: OrgSettingsChangedField[] }) {
  const { t } = useTranslation();
  if (changedFields.length === 0) return null;

  const tabs = orgSettingsChangedTabs(changedFields);
  const listFormat = new Intl.ListFormat(undefined, { type: "conjunction" });
  const detail =
    tabs.length > 1
      ? t("iam.orgDrawer.footer.acrossTabs", "across {{tabs}}", {
          tabs: listFormat.format(tabs.map((tab) => orgSettingsTabLabel(t, tab))),
        })
      : changedFields.map((field) => orgSettingsFieldLabel(t, field)).join(", ");

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 text-xs" data-testid="org-settings-dirty-status">
      <span aria-hidden className="size-2 shrink-0 rounded-full bg-warning-subtle-foreground" />
      <span className="shrink-0 text-foreground">
        {t("iam.orgDrawer.footer.unsavedChanges", {
          count: changedFields.length,
          defaultValue_one: "{{count}} unsaved change",
          defaultValue_other: "{{count}} unsaved changes",
        })}
      </span>
      <span className="truncate text-muted-foreground">· {detail}</span>
    </div>
  );
}
